import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import "./AdminDashboard.css";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";

export default function AdminDashboard() {
  const [clients, setClients] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [bulkOfficer, setBulkOfficer] = useState("");
  const [showOfficerEdit, setShowOfficerEdit] = useState(false);
  const [editOfficerInfo, setEditOfficerInfo] = useState({});
  const [editOfficerEmail, setEditOfficerEmail] = useState("");
  const [newOfficerEmail, setNewOfficerEmail] = useState("");
  const [showOfficerDelete, setShowOfficerDelete] = useState(null);
  const [newOfficerPassword, setNewOfficerPassword] = useState("");

  // Responsive: Track mobile/desktop
  const [isMobile, setIsMobile] = useState(window.innerWidth < 800);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 800);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const clientSnap = await getDocs(collection(db, "clients"));
    const officerSnap = await getDocs(collection(db, "officers"));
    const clientList = clientSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const officerList = officerSnap.docs.map((d) => ({
      ...d.data(),
      email: d.id,
    }));
    setClients(clientList);
    setOfficers(officerList);

    // Build assignments: { officerEmail: count }
    const assign = {};
    clientList.forEach((c) => {
      if (c.assignedOfficer) {
        assign[c.assignedOfficer] = (assign[c.assignedOfficer] || 0) + 1;
      }
    });
    setAssignments(assign);

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    fetchData();
  };

  // --- Filtering/Search ---
  const filteredClients = clients.filter((c) => {
    const officerObj = officers.find((o) => o.email === c.assignedOfficer);
    const name =
      (c.formDataPurchase?.firstName || c.formDataRefinance?.firstName || "") +
      " " +
      (c.formDataPurchase?.lastName || c.formDataRefinance?.lastName || "");
    const email = c.formDataPurchase?.email || c.formDataRefinance?.email || "";
    const phone = c.formDataPurchase?.phone || c.formDataRefinance?.phone || "";
    const status =
      c.officerApproved === true
        ? "approved"
        : c.officerApproved === false
        ? "denied"
        : "pending";
    return (
      name.toLowerCase().includes(clientSearch.toLowerCase()) ||
      email.toLowerCase().includes(clientSearch.toLowerCase()) ||
      phone.toLowerCase().includes(clientSearch.toLowerCase()) ||
      status.includes(clientSearch.toLowerCase()) ||
      (officerObj &&
        ((officerObj.name || "")
          .toLowerCase()
          .includes(clientSearch.toLowerCase()) ||
          officerObj.email.toLowerCase().includes(clientSearch.toLowerCase())))
    );
  });

  const allClientsSelected =
    filteredClients.length > 0 &&
    filteredClients.every((c) => selectedClientIds.includes(c.id));

  const toggleClientSelection = (clientId) => {
    setSelectedClientIds((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAllClients = () => {
    if (allClientsSelected) {
      setSelectedClientIds((prev) =>
        prev.filter((id) => !filteredClients.map((c) => c.id).includes(id))
      );
    } else {
      setSelectedClientIds((prev) =>
        Array.from(new Set([...prev, ...filteredClients.map((c) => c.id)]))
      );
    }
  };

  // --- Assign Officer ---
  const handleAssignOfficer = async (clientId, officerEmail) => {
    await updateDoc(doc(db, "clients", clientId), {
      assignedOfficer: officerEmail || "",
    });
    setClients((prev) =>
      prev.map((c) =>
        c.id === clientId ? { ...c, assignedOfficer: officerEmail || "" } : c
      )
    );
    // Recalculate assignments
    const assign = {};
    clients.forEach((c) => {
      const assigned =
        c.id === clientId ? officerEmail || "" : c.assignedOfficer || "";
      if (assigned) {
        assign[assigned] = (assign[assigned] || 0) + 1;
      }
    });
    setAssignments(assign);
  };

  // --- Bulk Assign ---
  const handleBulkAssign = async () => {
    if (!bulkOfficer) return;
    await Promise.all(
      selectedClientIds.map((id) =>
        updateDoc(doc(db, "clients", id), {
          assignedOfficer: bulkOfficer,
        })
      )
    );
    setClients((prev) =>
      prev.map((c) =>
        selectedClientIds.includes(c.id)
          ? { ...c, assignedOfficer: bulkOfficer }
          : c
      )
    );
    setSelectedClientIds([]);
    setBulkOfficer("");
    // Recalculate assignments
    const assign = {};
    clients.forEach((c) => {
      const assigned = selectedClientIds.includes(c.id)
        ? bulkOfficer
        : c.assignedOfficer || "";
      if (assigned) {
        assign[assigned] = (assign[assigned] || 0) + 1;
      }
    });
    setAssignments(assign);
  };

  // --- Edit Officer Modal ---
  const openEditOfficer = (officer) => {
    setEditOfficerInfo(officer);
    setEditOfficerEmail(officer.email);
    setShowOfficerEdit(true);
  };

  const handleEditOfficerChange = (field, value) => {
    setEditOfficerInfo((info) => ({
      ...info,
      [field]: value,
    }));
  };

  const handleEditOfficerSubmit = async (e) => {
    e.preventDefault();
    await updateDoc(doc(db, "officers", editOfficerEmail), editOfficerInfo);
    setOfficers((prev) =>
      prev.map((o) =>
        o.email === editOfficerEmail
          ? { ...editOfficerInfo, email: editOfficerEmail }
          : o
      )
    );
    setShowOfficerEdit(false);
  };

  // --- Remove Officer and Unassign Clients ---
  const handleDeleteOfficer = async (email) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this officer? This cannot be undone."
      )
    )
      return;
    await deleteDoc(doc(db, "officers", email));

    // Unassign this officer from all clients
    const clientSnap = await getDocs(collection(db, "clients"));
    const batch = [];
    clientSnap.forEach((docSnap) => {
      const client = docSnap.data();
      if (client.assignedOfficer === email) {
        batch.push(
          updateDoc(doc(db, "clients", docSnap.id), { assignedOfficer: "" })
        );
      }
    });
    await Promise.all(batch);

    setOfficers((prev) => prev.filter((o) => o.email !== email));
    setShowOfficerDelete(null);
    // Also update your clients state to reflect the changes in the UI
    setClients((prev) =>
      prev.map((c) =>
        c.assignedOfficer === email ? { ...c, assignedOfficer: "" } : c
      )
    );
  };

  // --- Add Officer ---
  const handleAddOfficer = async (e) => {
    e.preventDefault();
    if (!newOfficerEmail || !newOfficerPassword) return;

    const auth = getAuth();
    try {
      // 1. Create officer in Firebase Auth
      await createUserWithEmailAndPassword(
        auth,
        newOfficerEmail,
        newOfficerPassword
      );

      // 2. Store officer info in Firestore
      await setDoc(doc(db, "officers", newOfficerEmail.toLowerCase()), {
        email: newOfficerEmail.toLowerCase(),
        name: "",
        nmlsNumber: "",
        mloNumber: "",
        phone: "",
        facebook: "",
        instagram: "",
        tiktok: "",
      });
      setOfficers((prev) => [
        ...prev,
        { email: newOfficerEmail.toLowerCase(), name: "" },
      ]);
      alert(
        "Officer added successfully!\nTemporary password is: " +
          newOfficerPassword +
          "\nProvide these credentials to the officer to log in."
      );
      setNewOfficerEmail("");
      setNewOfficerPassword("");
    } catch (err) {
      alert("Failed to add officer: " + err.message);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/admin-login";
  };

  if (loading) {
    return <div className="admin-dashboard-loading">Loading...</div>;
  }

  return (
    <main className="admin-dashboard-main">
      <header className="admin-dashboard-header">
        <h1 className="admin-dashboard-title">Admin Dashboard</h1>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            onClick={handleRefresh}
            className="admin-dashboard-refresh"
            title="Refresh"
            aria-label="Refresh"
          >
            &#x21bb; {/* Unicode for a refresh/reload icon */}
          </button>
          <button onClick={handleLogout} className="admin-dashboard-logout">
            Logout
          </button>
        </div>
      </header>

      {/* --- Add Officer Section --- */}
      <section className="admin-card-section">
        <h3>Add Officer</h3>
        <form
          onSubmit={handleAddOfficer}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 12,
            maxWidth: 320,
          }}
        >
          <input
            type="email"
            placeholder="Officer Email"
            value={newOfficerEmail}
            onChange={(e) => setNewOfficerEmail(e.target.value)}
            required
            style={{ width: "100%" }}
          />
          <input
            type="password"
            placeholder="Temporary Password"
            value={newOfficerPassword}
            onChange={(e) => setNewOfficerPassword(e.target.value)}
            required
            minLength={6}
            style={{ width: "100%" }}
          />
          <button
            type="submit"
            className="admin-add-officer-btn"
            style={{ width: "55%" }}
          >
            Add Officer
          </button>
        </form>
      </section>

      {/* --- Officer List Section --- */}
      <section className="admin-card-section">
        <h3>Officers</h3>
        <ul className="admin-officer-list">
          {officers.map((o) => (
            <li key={o.email} className="admin-officer-list-item">
              <span className="admin-officer-list-name">
                {o.name || o.email}
              </span>
              <span className="admin-officer-list-clients">
                — {assignments[o.email] || 0} assigned client(s)
              </span>
              <span className="admin-officer-actions">
                <button
                  onClick={() => openEditOfficer(o)}
                  className="admin-officer-edit-btn"
                >
                  Edit
                </button>
                <button
                  onClick={() => setShowOfficerDelete(o.email)}
                  className="admin-officer-delete-btn"
                >
                  Remove
                </button>
              </span>

              {/* Officer Delete Confirmation Modal - inside the map, so "o" is defined */}
              {showOfficerDelete === o.email && (
                <div className="admin-modal-overlay">
                  <div className="admin-modal-content">
                    <p>
                      Are you sure you want to delete officer{" "}
                      <strong>{o.name || o.email}</strong>?
                    </p>
                    <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                      <button
                        onClick={() => handleDeleteOfficer(o.email)}
                        className="admin-officer-delete-btn"
                      >
                        Yes, Delete
                      </button>
                      <button
                        onClick={() => setShowOfficerDelete(null)}
                        className="admin-officer-edit-btn"
                        style={{ background: "#eee", color: "#222" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* --- Bulk Assign Section --- */}
      <section className="admin-bulk-assign-section">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={handleBulkAssign}
            disabled={selectedClientIds.length === 0 || !bulkOfficer}
            className="admin-bulk-assign-btn"
            style={{
              opacity: selectedClientIds.length === 0 || !bulkOfficer ? 0.6 : 1,
            }}
          >
            Assign Selected Clients
          </button>
          <select
            value={bulkOfficer}
            onChange={(e) => setBulkOfficer(e.target.value)}
          >
            <option value="">Choose Officer</option>
            {officers.map((o) => (
              <option key={o.email} value={o.email}>
                {o.name || o.email}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* --- Client Table --- */}
      <section className="admin-client-table-section">
        <div className="admin-table-controls">
          <input
            type="text"
            placeholder="Search clients by name, email, officer, or status..."
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
          />
        </div>
        <div className="admin-client-table-scroll">
          {/* DESKTOP TABLE */}
          {!isMobile && (
            <table className="admin-clients-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allClientsSelected}
                      onChange={handleSelectAllClients}
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Officer</th>
                  <th>Last Note</th>
                  <th>Last Contacted</th>
                  <th>Assign Officer</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => {
                  const name =
                    (client.formDataPurchase?.firstName ||
                      client.formDataRefinance?.firstName ||
                      "") +
                    " " +
                    (client.formDataPurchase?.lastName ||
                      client.formDataRefinance?.lastName ||
                      "");
                  const email =
                    client.formDataPurchase?.email ||
                    client.formDataRefinance?.email ||
                    "";
                  const phone =
                    client.formDataPurchase?.phone ||
                    client.formDataRefinance?.phone ||
                    "";
                  const status =
                    client.officerApproved === true
                      ? "Approved"
                      : client.officerApproved === false
                      ? "Denied"
                      : "Pending";
                  const officer =
                    officers.find((o) => o.email === client.assignedOfficer)
                      ?.name ||
                    client.assignedOfficer ||
                    "";
                  const lastNote = client.lastNoteToClient || "";
                  const lastContacted = client.lastContactedAt
                    ? new Date(
                        client.lastContactedAt.seconds
                          ? client.lastContactedAt.seconds * 1000
                          : client.lastContactedAt
                      ).toLocaleDateString()
                    : "--";
                  return (
                    <tr key={client.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedClientIds.includes(client.id)}
                          onChange={() => toggleClientSelection(client.id)}
                        />
                      </td>
                      <td>{name}</td>
                      <td>{email}</td>
                      <td>{phone}</td>
                      <td>{status}</td>
                      <td>{officer}</td>
                      <td style={{ maxWidth: 160, whiteSpace: "pre-line" }}>
                        {lastNote}
                      </td>
                      <td>{lastContacted}</td>
                      <td>
                        <select
                          value={client.assignedOfficer || ""}
                          onChange={(e) =>
                            handleAssignOfficer(client.id, e.target.value)
                          }
                        >
                          <option value="">Unassign</option>
                          {officers.map((o) => (
                            <option key={o.email} value={o.email}>
                              {o.name || o.email}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* MOBILE CARD VIEW */}
          {isMobile && (
            <div className="admin-client-cards">
              {filteredClients.map((client) => {
                const name =
                  (client.formDataPurchase?.firstName ||
                    client.formDataRefinance?.firstName ||
                    "") +
                  " " +
                  (client.formDataPurchase?.lastName ||
                    client.formDataRefinance?.lastName ||
                    "");
                const email =
                  client.formDataPurchase?.email ||
                  client.formDataRefinance?.email ||
                  "";
                const phone =
                  client.formDataPurchase?.phone ||
                  client.formDataRefinance?.phone ||
                  "";
                const status =
                  client.officerApproved === true
                    ? "Approved"
                    : client.officerApproved === false
                    ? "Denied"
                    : "Pending";
                const officer =
                  officers.find((o) => o.email === client.assignedOfficer)
                    ?.name ||
                  client.assignedOfficer ||
                  "";
                const lastNote = client.lastNoteToClient || "";
                const lastContacted = client.lastContactedAt
                  ? new Date(
                      client.lastContactedAt.seconds
                        ? client.lastContactedAt.seconds * 1000
                        : client.lastContactedAt
                    ).toLocaleDateString()
                  : "--";
                return (
                  <div key={client.id} className="admin-client-card">
                    <div className="admin-client-card-row">
                      <strong>Name:</strong> <span>{name}</span>
                    </div>
                    <div className="admin-client-card-row">
                      <strong>Email:</strong> <span>{email}</span>
                    </div>
                    <div className="admin-client-card-row">
                      <strong>Phone:</strong> <span>{phone}</span>
                    </div>
                    <div className="admin-client-card-row">
                      <strong>Status:</strong> <span>{status}</span>
                    </div>
                    <div className="admin-client-card-row">
                      <strong>Officer:</strong> <span>{officer}</span>
                    </div>
                    <div className="admin-client-card-row">
                      <strong>Last Note:</strong>{" "}
                      <span style={{ whiteSpace: "pre-line" }}>{lastNote}</span>
                    </div>
                    <div className="admin-client-card-row">
                      <strong>Last Contacted:</strong>{" "}
                      <span>{lastContacted}</span>
                    </div>
                    <div className="admin-client-card-actions">
                      <select
                        value={client.assignedOfficer || ""}
                        onChange={(e) =>
                          handleAssignOfficer(client.id, e.target.value)
                        }
                      >
                        <option value="">Unassign</option>
                        {officers.map((o) => (
                          <option key={o.email} value={o.email}>
                            {o.name || o.email}
                          </option>
                        ))}
                      </select>
                      <input
                        type="checkbox"
                        checked={selectedClientIds.includes(client.id)}
                        onChange={() => toggleClientSelection(client.id)}
                        title="Select for bulk assign"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* --- Officer Edit Modal --- */}
      {showOfficerEdit && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <h2>Edit Officer</h2>
            <form onSubmit={handleEditOfficerSubmit}>
              {[
                "name",
                "nmlsNumber",
                "mloNumber",
                "phone",
                "facebook",
                "instagram",
                "tiktok",
              ].map((field) => (
                <div key={field} style={{ marginBottom: 12 }}>
                  <label
                    style={{
                      fontWeight: 600,
                      minWidth: 90,
                      display: "inline-block",
                    }}
                  >
                    {field.charAt(0).toUpperCase() + field.slice(1)}:
                  </label>
                  <input
                    type="text"
                    value={editOfficerInfo[field] ?? ""}
                    onChange={(e) =>
                      handleEditOfficerChange(field, e.target.value)
                    }
                    style={{
                      padding: "0.5em",
                      borderRadius: 6,
                      border: "1px solid #0096b2",
                      width: "75%",
                    }}
                  />
                </div>
              ))}
              <div style={{ display: "flex", gap: 12 }}>
                <button type="submit" className="admin-officer-save-btn">
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setShowOfficerEdit(false)}
                  className="admin-officer-edit-btn"
                  style={{ background: "#eee", color: "#222" }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
